/**
 * NotificationBell component - shows notification count badge and triggers dropdown
 */

import { useEffect, useCallback, useRef } from 'react';
import { clsx } from 'clsx';
import { Bell } from 'lucide-react';
import { useCommentStore } from '@stores/commentStore';
import { useBrowserNotifications } from '@hooks/useBrowserNotifications';

interface NotificationBellProps {
  onClick: () => void;
  isOpen?: boolean;
}

export default function NotificationBell({ onClick, isOpen = false }: NotificationBellProps) {
  const { unreadCount, fetchUnreadCount, notifications, fetchNotifications } = useCommentStore();
  const { isEnabled: browserNotificationsEnabled, show: showBrowserNotification } =
    useBrowserNotifications();
  const previousUnreadCount = useRef<number>(unreadCount);

  // Poll for unread count periodically
  useEffect(() => {
    void fetchUnreadCount();

    const interval = setInterval(() => {
      void fetchUnreadCount();
    }, 60000); // Every minute

    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Show browser notification when new notifications arrive
  useEffect(() => {
    const checkForNewNotifications = async () => {
      // If unread count increased and browser notifications are enabled
      if (
        browserNotificationsEnabled &&
        unreadCount > previousUnreadCount.current &&
        previousUnreadCount.current >= 0
      ) {
        // Fetch the latest notifications to show the new one
        await fetchNotifications(true); // unreadOnly = true

        // Find the newest unread notification
        const unreadNotifications = notifications.filter((n) => !n.isRead);
        const newestNotification = unreadNotifications[0];

        if (newestNotification) {
          // Show browser notification
          void showBrowserNotification({
            title: newestNotification.title,
            body: newestNotification.message,
            tag: `notification-${newestNotification.id}`,
            data: {
              notificationId: newestNotification.id,
              actionUrl: newestNotification.actionUrl,
            },
            requireInteraction: newestNotification.priority === 'urgent',
            onClick: () => {
              // The notification click will be handled by the browser notification service
              // which will navigate to the action URL
              if (newestNotification.actionUrl) {
                window.location.href = newestNotification.actionUrl;
              }
            },
          });
        }
      }

      // Update previous count
      previousUnreadCount.current = unreadCount;
    };

    void checkForNewNotifications();
  }, [
    unreadCount,
    browserNotificationsEnabled,
    notifications,
    fetchNotifications,
    showBrowserNotification,
  ]);

  const handleClick = useCallback(() => {
    onClick();
  }, [onClick]);

  return (
    <button
      onClick={handleClick}
      className={clsx(
        'relative rounded-lg p-2 transition-colors',
        'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900',
        'dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-white',
        isOpen && 'bg-neutral-100 dark:bg-neutral-800'
      )}
      aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
    >
      <Bell className="h-5 w-5" />

      {/* Unread badge */}
      {unreadCount > 0 && (
        <span
          className={clsx(
            'absolute -right-0.5 -top-0.5 flex items-center justify-center',
            'min-w-[18px] h-[18px] rounded-full px-1',
            'bg-red-500 text-[10px] font-bold text-white',
            'animate-in zoom-in duration-200'
          )}
        >
          {unreadCount > 99 ? '99+' : unreadCount}
        </span>
      )}
    </button>
  );
}
